import { PrimaryControlsStripLeading } from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.back";
import { ToolbarMoreActions } from "@-/dev.improve.haniverse.components.ui.toolbar.button.icon";
import {
  ToolbarPopoverButton,
  ToolbarPopoverChoiceRow,
} from "@-/dev.improve.haniverse.components.ui.toolbar.button.popover";
import { ToolbarViewButton } from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import type { CardFilters } from "@-/dev.improve.haniverse.lib.cards.filter";
import { useActorUser } from "@-/dev.improve.haniverse.lib.user";
import { CardFilterMenu } from "@-/dev.improve.haniverse.routes.nav.app._components.card-filter-menu";
import { useBrowseCharactersRankPageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.characters.navigator";
import { useBrowseCharactersHskPageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.characters.grade.hsk.navigator";
import { useBrowseCharactersTbclPageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.characters.grade.tbcl.navigator";
import { usePathname } from "@-/com.npmjs.expo-router";
import type { Dispatch, SetStateAction } from "react";
import { isNonEmpty } from "@-/com.npmjs.ts-array-length";

export type CharactersRoute = "rank" | "grade";

export interface CharactersToolbarProps {
  active: CharactersRoute | null;
}

export interface CharactersToolbarSortOption<SortMode extends string> {
  label: string;
  value: SortMode;
}

export function CharactersToolbar({ active }: CharactersToolbarProps) {
  const { hanziScript } = useActorUser();
  const gradeLabel = hanziScript === "traditional" ? "By TBCL" : "By HSK";

  const rankNavigator = useBrowseCharactersRankPageNavigator();
  const hskNavigator = useBrowseCharactersHskPageNavigator();
  const tbclNavigator = useBrowseCharactersTbclPageNavigator();
  const replaceRank = rankNavigator.replace.mutate;
  const replaceHsk = hskNavigator.replace.mutate;
  const replaceTbcl = tbclNavigator.replace.mutate;

  // Touch the pathname so this bar re-derives the active pill on route changes from outside the bar
  // (e.g. the browser back button on web).
  usePathname();

  return (
    <>
      <ToolbarViewButton
        label="By Rank"
        onPress={() => replaceRank()}
        selected={active === "rank"}
      />
      <ToolbarViewButton
        label={gradeLabel}
        onPress={() => (hanziScript === "traditional" ? replaceTbcl() : replaceHsk())}
        selected={active === "grade"}
      />
    </>
  );
}

/** Leading controls owned by the rendered Characters page, not the shared layout. */
export function CharactersToolbarActions<SortMode extends string = string>({
  cardFilters,
  onCardFiltersChange,
  onSortModeChange,
  sortMode,
  sortOptions,
}: {
  cardFilters: CardFilters;
  onCardFiltersChange: Dispatch<SetStateAction<CardFilters>>;
  onSortModeChange?: (value: SortMode) => void;
  sortMode?: SortMode;
  sortOptions?: readonly CharactersToolbarSortOption<SortMode>[];
}) {
  const showSort = sortOptions !== undefined && isNonEmpty(sortOptions) && sortMode !== undefined;

  return (
    <PrimaryControlsStripLeading>
      <ToolbarMoreActions>
        {showSort && (
          <ToolbarPopoverButton accessibilityLabel="Sort characters" label="Sort" name="MdSort">
            <ToolbarPopoverChoiceRow
              closeOnSelect
              label="Sort by"
              layout="stacked"
              onValueChange={(value) => onSortModeChange?.(value)}
              options={sortOptions}
              value={sortMode}
            />
          </ToolbarPopoverButton>
        )}
        <CardFilterMenu
          accessibilityLabel="Filter characters"
          filters={cardFilters}
          onFiltersChange={onCardFiltersChange}
        />
      </ToolbarMoreActions>
    </PrimaryControlsStripLeading>
  );
}
